import { useAuth } from '../../contexts/AuthContext';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export const Header = ({ 
    title, 
    subtitle,
    selectedDate, 
    onDateChange,
    showDatePicker = true,
    displayMode = 'HT',
    onDisplayModeChange
}) => {
    const { user } = useAuth();

    const handleSelect = (date) => {
        if (date) {
            const dateStr = format(date, 'yyyy-MM-dd');
            onDateChange(dateStr);
        }
    };

    const handleModeToggle = (checked) => {
        if (onDisplayModeChange) {
            onDisplayModeChange(checked ? 'TTC' : 'HT');
        }
    };

    return (
        <header 
            className="h-16 border-b border-border bg-card px-6 flex items-center justify-between"
            data-testid="header"
        >
            <div>
                <h1 className="text-xl font-heading font-semibold text-foreground">
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                )}
            </div>

            <div className="flex items-center gap-6">
                {/* Toggle TTC/HT */}
                {showDatePicker && onDisplayModeChange && (
                    <div className="flex items-center gap-3 bg-secondary/50 px-4 py-2 rounded-lg" data-testid="display-mode-toggle">
                        <Label 
                            htmlFor="mode-toggle" 
                            className={cn(
                                "text-sm font-medium cursor-pointer transition-colors",
                                displayMode === 'HT' ? "text-foreground" : "text-muted-foreground"
                            )}
                        >
                            HT
                        </Label>
                        <Switch
                            id="mode-toggle"
                            checked={displayMode === 'TTC'}
                            onCheckedChange={handleModeToggle}
                            data-testid="ttc-ht-switch"
                        />
                        <Label 
                            htmlFor="mode-toggle" 
                            className={cn(
                                "text-sm font-medium cursor-pointer transition-colors",
                                displayMode === 'TTC' ? "text-foreground" : "text-muted-foreground"
                            )}
                        >
                            TTC
                        </Label>
                    </div>
                )}

                {showDatePicker && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    "justify-start text-left font-normal min-w-[240px]",
                                    !selectedDate && "text-muted-foreground"
                                )}
                                data-testid="date-picker-trigger"
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {selectedDate ? (
                                    format(new Date(selectedDate), "EEEE d MMMM yyyy", { locale: fr })
                                ) : (
                                    <span>Sélectionner une date</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                            <Calendar
                                mode="single"
                                selected={selectedDate ? new Date(selectedDate) : undefined}
                                onSelect={handleSelect}
                                locale={fr}
                                initialFocus
                                data-testid="calendar"
                            />
                        </PopoverContent>
                    </Popover>
                )}

                <div className="text-right">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
            </div>
        </header>
    );
};

export default Header;
